import { ApexTextInputComponent } from '@fmr-pr103625/apex-text-input';
import { apexCustomElement } from '@fmr-pr103625/apex-core';
import { ApexSlotableMixin } from '@fmr-pr103625/apex-slots';
import { ApexValidationRuleTypes, ApexValidationRuleNumericMax, ApexValidationRuleNumericMin } from '@fmr-pr103625/apex-validations';

/******************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */

function __decorate(decorators, target, key, desc) {
  var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
  if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
  else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
  return c > 3 && r && Object.defineProperty(target, key, r), r;
}

function __metadata(metadataKey, metadataValue) {
  if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(metadataKey, metadataValue);
}

typeof SuppressedError === "function" ? SuppressedError : function (error, suppressed, message) {
  var e = new Error(message);
  return e.name = "SuppressedError", e.error = error, e.suppressed = suppressed, e;
};

/**
 * @license
 * Copyright 2017 Google LLC
 * SPDX-License-Identifier: BSD-3-Clause
 */
const i=(i,e)=>"method"===e.kind&&e.descriptor&&!("value"in e.descriptor)?{...e,finisher(n){n.createProperty(e.key,i);}}:{kind:"field",key:Symbol(),placement:"own",descriptor:{},originalKey:e.key,initializer(){"function"==typeof e.initializer&&(this[e.key]=e.initializer.call(this));},finisher(n){n.createProperty(e.key,i);}},e=(i,e,n)=>{e.constructor.createProperty(n,i);};function n$1(n){return (t,o)=>void 0!==o?e(n,t,o):i(n,t)}

/**
 * @license
 * Copyright 2021 Google LLC
 * SPDX-License-Identifier: BSD-3-Clause
 */var n;null!=(null===(n=window.HTMLSlotElement)||void 0===n?void 0:n.prototype.assignedElements)?(o,n)=>o.assignedElements(n):(o,n)=>o.assignedNodes(n).filter((o=>o.nodeType===Node.ELEMENT_NODE));

var version = "3.7.0";

class ApexNumericComponent extends ApexTextInputComponent {
    constructor() {
        super(...arguments);
        //#region PROPERTIES
        this.label = 'Number';
        this.decimalPlaces = 0;
        this.allowNegative = false;
        this.showDelimiter = false;
        this._min = 0;
        this._max = undefined;
        this._required = false;
        this.inputType = 'text';
        // can be made a property later when we want to take localization into account
        this.locale = 'en-US';
        this._validationRules = {
            validations: {}
        };
        this._validationText = {
            validations: {
                required: 'Number required.'
            }
        };
        this.inputMode = 'number';
        //#endregion
    }
    get min() {
        return this._min;
    }
    set min(newValue) {
        const oldValue = this.min;
        this._min = newValue;
        this._validationRules.validations.numericMin = this.min;
        this._validationText.validations.numericMin =
            this._validationText.validations.numericMin || `Value entered must be greater than or equal to ${this.min}`;
        this.updateValidationRule(ApexValidationRuleTypes.NUMERIC_MIN);
        this.requestUpdate('min', oldValue);
    }
    get max() {
        return this._max;
    }
    set max(newValue) {
        const oldValue = this.max;
        this._max = newValue;
        this._validationRules.validations.numericMax = this.max;
        this._validationText.validations.numericMax =
            this._validationText.validations.numericMax || `Value entered must be less than or equal to ${this.max}`;
        this.updateValidationRule(ApexValidationRuleTypes.NUMERIC_MAX);
        this.requestUpdate('max', oldValue);
    }
    get required() {
        return this._required;
    }
    set required(newValue) {
        this._required = newValue;
        this._validationRules.validations.required = this.required;
        this.updateValidationRule(ApexValidationRuleTypes.REQUIRED);
    }
    get validatableValue() {
        return Number(this.value);
    }
    //#endregion
    //#region LIFE CYCLE HOOKS
    connectedCallback() {
        super.connectedCallback();
        if (this.value) {
            this.formatValue();
        }
    }
    triggerFormatValue() {
        const tempInput = document.createElement('input');
        tempInput.value = this.value;
        tempInput.addEventListener('init-value', (e) => {
            this.formatValue(e);
        });
        const tempEvent = new Event('init-value');
        tempInput.dispatchEvent(tempEvent);
    }
    firstUpdated() {
        super.firstUpdated();
        this.classList.add('apex3-numeric');
    }
    //#endregion
    //#region METHODS
    formatValue(event) {
        if (event) {
            this.value = event.target.value;
        }
        this.value = this.value.replace(/[^\d-.]/g, '');
        if (!this.allowNegative) {
            this.value = this.value.replace(/-/g, '');
        }
        const decimalSplit = this.value.split('.');
        const endsWithDecimal = this.value[this.value.length - 1] === '.';
        // eslint-disable-next-line prefer-destructuring
        this.value = decimalSplit[0];
        let afterDecimal;
        if (this.decimalPlaces && decimalSplit.length > 1) {
            afterDecimal = decimalSplit.slice(1).join('');
            this.value = `${this.value}.${afterDecimal.substring(0, this.decimalPlaces)}`;
        }
        this.formattedValue = this.value;
        if (this.showDelimiter && this.value) {
            // if all of the numbers after the decimal are zero, this will remove them - need to add them back after
            this.formattedValue = Number(this.value).toLocaleString(this.locale);
            if (endsWithDecimal) {
                this.formattedValue = `${this.formattedValue}.`;
            }
            // put zeros back on the end
            if (this.decimalPlaces && afterDecimal && Number(afterDecimal) === 0) {
                this.formattedValue = `${this.formattedValue}.${afterDecimal.substring(0, this.decimalPlaces)}`;
            }
        }
        if (event) {
            // eslint-disable-next-line no-param-reassign
            event.target.value = this.formattedValue;
        }
        this.dispatchUpdateEvent();
    }
    handleInput(event) {
        if (event && event.target) {
            this.formatValue(event);
        }
    }
    revalidate(event) {
        if (event && event.target) {
            this.formatValue(event);
            this.validate();
        }
    }
    updateValidationRule(rule) {
        // we have customized rules for min and max so we don't call the parent for them
        if (rule !== ApexValidationRuleTypes.NUMERIC_MIN && rule !== ApexValidationRuleTypes.NUMERIC_MAX) {
            super.updateValidationRule(rule);
            return;
        }
        const { validations: text } = this._validationText;
        let ruleConfig;
        switch (rule) {
            case ApexValidationRuleTypes.NUMERIC_MIN:
                if (this.min) {
                    ruleConfig = new ApexValidationRuleNumericMin(text[rule], this.min);
                }
                this.updateRule(rule, ruleConfig);
                break;
            case ApexValidationRuleTypes.NUMERIC_MAX:
                if (this.max) {
                    ruleConfig = new ApexValidationRuleNumericMax(text[rule], this.max);
                }
                this.updateRule(rule, ruleConfig);
                break;
        }
    }
}
ApexNumericComponent.version = version;
__decorate([
    n$1({ type: String, attribute: 'label' }),
    __metadata("design:type", Object)
], ApexNumericComponent.prototype, "label", void 0);
__decorate([
    n$1({ type: Number, attribute: 'decimal-places' }),
    __metadata("design:type", Object)
], ApexNumericComponent.prototype, "decimalPlaces", void 0);
__decorate([
    n$1({ type: Boolean, attribute: 'allow-negative' }),
    __metadata("design:type", Object)
], ApexNumericComponent.prototype, "allowNegative", void 0);
__decorate([
    n$1({ type: Boolean, attribute: 'show-delimiter' }),
    __metadata("design:type", Object)
], ApexNumericComponent.prototype, "showDelimiter", void 0);
__decorate([
    n$1({ type: Number, attribute: 'min' }),
    __metadata("design:type", Number),
    __metadata("design:paramtypes", [Number])
], ApexNumericComponent.prototype, "min", null);
__decorate([
    n$1({ type: Number, attribute: 'max' }),
    __metadata("design:type", Number),
    __metadata("design:paramtypes", [Number])
], ApexNumericComponent.prototype, "max", null);
__decorate([
    n$1({ type: Boolean, attribute: 'required' }),
    __metadata("design:type", Boolean),
    __metadata("design:paramtypes", [Boolean])
], ApexNumericComponent.prototype, "required", null);
const ApexCustomElementMixin = apexCustomElement({
    namespace: 'apex',
    shadowDOM: false,
    name: 'numeric',
    pvdNamespace: 'pvd'
});
class ApexNumeric extends ApexSlotableMixin(ApexCustomElementMixin(ApexNumericComponent)) {
}

export { ApexNumeric, ApexNumericComponent };
//# sourceMappingURL=component.js.map
